import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Form from '../../antd/form/Form/Form.jsx';
import CollapseBlock from '../blocks/CollapseBlock/CollapseBlock.jsx';
import FormTextField from '../../antd/form/FormTextField/FormTextField.jsx';
import { email, required, url } from '../../antd/form/FormValidation.js';
import FormButton from '../../antd/form/FormButton/FormButton.jsx';
import FormButtonsContainer from '../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import PropTypes from 'prop-types';
import { Col, Row } from 'antd';
import FormSelect from '../../antd/form/FormSelect/FormSelect.jsx';
import { useGetExtPartnersRolesQuery } from '../../../redux/services/roles.jsx';
import useUserHasRole from '../../../hooks/useUserHasRoles.jsx';
import { ROLE_EXTERNAL_PARTNERS_ADMIN } from '../../../utils/constants/roles.js';
import FormHiddenField from '../../antd/form/FormHiddenField/FormHiddenField.jsx';
import FormCheckBox from '../../antd/form/FormCheckBox/FormCheckBox.jsx';
import {
  StyledModal,
  StyledModalHeader,
  ButtonContainer,
  MainButton,
} from '../../common/form/FormElements.jsx';
import GDPRInfoContent from './GDPRInfoContent.jsx';

const ExternalPartnersForm = ({ initials, onSubmit }) => {
  const { t } = useTranslation();
  const isExtPartnersAdmin = useUserHasRole(ROLE_EXTERNAL_PARTNERS_ADMIN);

  // Enable reCAPTCHA only on the specific route
  const isRecaptchaRoute =
    typeof window !== 'undefined' &&
    window.location.pathname === '/registracia-externeho-partnera-fri';

  const { data: roles } = useGetExtPartnersRolesQuery(undefined, {
    skip: !isExtPartnersAdmin,
  });

  const rolesOptions = useMemo(
    () =>
      roles &&
      roles.map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [roles],
  );

  // Načítanie reCAPTCHA skriptu len na vybranej URL
  useEffect(() => {
    let script;

    if (isRecaptchaRoute) {
      if (!window.grecaptcha) {
        script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${import.meta.env.VITE_RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          // Zobraz badge ak existuje
          const badge = document.querySelector('.grecaptcha-badge');
          if (badge) badge.style.display = '';
        };
        document.body.appendChild(script);
      } else {
        // Zobraz badge ak existuje
        const badge = document.querySelector('.grecaptcha-badge');
        if (badge) badge.style.display = '';
      }
    } else {
      // Nie sme na danej stránke, skry badge ak by existovala
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.display = 'none';
    }

    return () => {
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.display = 'none';
      if (script) document.body.removeChild(script);
    };
  }, [isRecaptchaRoute]);

  const handleFinish = (values) => {
    if (!isRecaptchaRoute || !window.grecaptcha) {
      onSubmit({ ...values, captchaResponse: '' });
      return;
    }

    window.grecaptcha.ready(() => {
      // reCAPTCHA execute vracia Promise s tokenom
      window.grecaptcha
        .execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, { action: 'contact' })
        .then((token) => {
          onSubmit({ ...values, captchaResponse: token });
        });
    });
  };

  // Skry ikony pri zobrazení Form len na danej URL
  useEffect(() => {
    if (!isRecaptchaRoute) return;

    const elements = document.getElementsByClassName(
      'application-questions-container',
    );
    const prevDisplays = [];
    for (let i = 0; i < elements.length; i++) {
      prevDisplays[i] = elements[i].style.display;
      elements[i].style.display = 'none';
    }
    return () => {
      for (let i = 0; i < elements.length; i++) {
        elements[i].style.display = prevDisplays[i] || '';
      }
    };
  }, [isRecaptchaRoute]);

  const [gdprOpen, setGdprOpen] = useState(false);

  return (
    <>
      {' '}
      {/* fragment wraps form and modal */}
      <Form onFinish={handleFinish} initialValues={initials}>
        <CollapseBlock
          collapsible
          collapse={false}
          title={t('Informácie o externom partnerovi FRI')}
        >
          <FormHiddenField name="id" />
          <FormHiddenField name="from" />

          <FormTextField
            label={t('Názov')}
            name="name"
            extra={isExtPartnersAdmin ? '' : t('Názov vašej firmy')}
            rules={[required(t)]}
          />

          <FormTextField
            label={t('Webová adresa')}
            name="web"
            extra={
              isExtPartnersAdmin
                ? ''
                : t('URL adresa firmy, napr. https://firma.sk')
            }
            rules={[url(t)]}
          />
        </CollapseBlock>
        <CollapseBlock
          collapsible
          collapse={false}
          title={t('Informácie o kontaktnej osobe')}
        >
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={4}>
              <FormTextField label={t('Titul pred')} name="title_before" />
            </Col>
            <Col span={8}>
              <FormTextField
                label={t('Krstné meno')}
                name="first_name"
                rules={[required(t)]}
              />
            </Col>
            <Col span={8}>
              <FormTextField
                label={t('Priezvisko')}
                name="last_name"
                rules={[required(t)]}
              />
            </Col>
            <Col span={4}>
              <FormTextField label={t('Titul za')} name="title_after" />
            </Col>
          </Row>
          <FormTextField
            label={t('E-mailová adresa')}
            name="email"
            extra={
              isExtPartnersAdmin
                ? ''
                : t(
                    'E-mailová adresa kontaktnej osoby, bude zároveň slúžiť aj ako prihlasovacie meno',
                  )
            }
            rules={[email(t), required(t)]}
          />

          <FormTextField
            label={t('Telefónne číslo')}
            name="phone_number"
            extra={
              isExtPartnersAdmin
                ? ''
                : t('Telefónne číslo kontaktnej osoby alebo kontakt na firmu')
            }
            rules={[required(t)]}
          />
        </CollapseBlock>

        {isExtPartnersAdmin && (
          <CollapseBlock title={t('Role')} collapsible>
            {rolesOptions && (
              <FormSelect
                label={t('Role používateľa')}
                name={'user_roles'}
                placeholder={t('Vyhľadať rolu')}
                options={rolesOptions}
                mode="multiple"
                rules={[required(t)]}
              />
            )}
          </CollapseBlock>
        )}

        {isRecaptchaRoute && (
          <FormCheckBox
            name="gdpr_consent"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          t(
                            'Je potrebné udeliť súhlas so spracovaním osobných údajov',
                          ),
                        ),
                      ),
              },
            ]}
            label={
              <span>
                {t('Súhlasím so spracovaním osobných údajov podľa ')}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setGdprOpen(true);
                  }}
                >
                  {t('podmienok spracúvania osobných údajov')}
                </a>
              </span>
            }
          />
        )}

        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'externalPartners'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </Form>
      <StyledModal
        open={gdprOpen}
        size="md"
        onCancel={() => setGdprOpen(false)}
        footer={null}
        width={900}
      >
        <StyledModalHeader>
          {t('Podmienky spracúvania osobných údajov')}
        </StyledModalHeader>
        <GDPRInfoContent />
        <ButtonContainer>
          <MainButton onClick={() => setGdprOpen(false)}>
            {t('Zatvoriť')}
          </MainButton>
        </ButtonContainer>
      </StyledModal>
    </>
  );
};

ExternalPartnersForm.propTypes = {
  initials: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  location: PropTypes.string,
};

export default ExternalPartnersForm;
